import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    IoCreateOutline,
    IoTrashOutline,
    IoOpenOutline,
    IoSearchOutline,
    IoCloseOutline,
    IoCheckmarkOutline
} from 'react-icons/io5';
import {
    formatDraftOptionLabel,
    getDraftTitle,
    isHiddenFromVersions
} from '../../utils/scheduleDraftKind';

const getDraftPeriodId = (draft) => {
    const raw = draft?.period_id ?? draft?.periodId;
    if (raw === null || raw === undefined || raw === '') {
        return null;
    }
    return raw;
};

const getDraftId = (draft) => {
    const raw = draft?.id;
    if (raw === null || raw === undefined || raw === '') {
        return null;
    }
    return raw;
};

const getPeriodActiveDraftId = (period) => {
    const raw = period?.active_schedule_draft_id ?? period?.activeScheduleDraftId;
    if (raw === null || raw === undefined || raw === '') {
        return null;
    }
    return raw;
};

const formatPeriodLabel = (period) => {
    if (!period) return 'Unscoped';

    const startWeek = period.start_week ?? period.startWeek;
    const endWeek = period.end_week ?? period.endWeek;
    if (startWeek != null && endWeek != null) {
        return `${period.name || 'Period'} · W${startWeek}–${endWeek}`;
    }

    return period.name || 'Period';
};

const formatRelativeDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';

    const diffMs = Date.now() - date.getTime();
    const diffMinutes = Math.round(diffMs / 60000);
    if (diffMinutes < 1) return 'just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;

    const diffHours = Math.round(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h ago`;

    const diffDays = Math.round(diffHours / 24);
    if (diffDays < 14) return `${diffDays}d ago`;

    return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
};

const formatAbsoluteDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const entryCountLabel = (count) => {
    const n = Number(count);
    if (!Number.isFinite(n) || n <= 0) return 'Empty';
    if (n === 1) return '1 entry';
    return `${n.toLocaleString()} entries`;
};

const DraftManagerView = ({
    currentDraftName,
    assetManager,
    draftManager,
    onModeChange,
    onDraftChange,
    onCurrentDraftDeleted,
    onOpenDraft,
    refreshTimePeriods,
    timePeriods = [],
    setError,
    setSuccess,
    setIsLoading
}) => {
    const [drafts, setDrafts] = useState([]);
    const [loadingDrafts, setLoadingDrafts] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterPeriodId, setFilterPeriodId] = useState('all');
    const [editingDraftName, setEditingDraftName] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [busyDraftName, setBusyDraftName] = useState(null);

    const loadDrafts = useCallback(async () => {
        if (!draftManager) return;
        setLoadingDrafts(true);
        try {
            const draftList = await draftManager.fetchDrafts();
            const visibleDrafts = (draftList || []).filter((draft) => !isHiddenFromVersions(draft));
            setDrafts(visibleDrafts);
        } catch (err) {
            console.error('Error loading drafts:', err);
            if (setError) setError('Failed to load drafts: ' + err.message);
        } finally {
            setLoadingDrafts(false);
        }
    }, [draftManager, setError]);

    useEffect(() => {
        loadDrafts();
    }, [loadDrafts]);

    useEffect(() => {
        if (!draftManager) return undefined;

        const handleDraftManagerChange = (state) => {
            if (!Array.isArray(state?.drafts)) return;
            const visibleDrafts = state.drafts.filter((draft) => !isHiddenFromVersions(draft));
            setDrafts(visibleDrafts);
        };

        draftManager.addListener(handleDraftManagerChange);
        return () => draftManager.removeListener(handleDraftManagerChange);
    }, [draftManager]);

    const periodById = useMemo(() => {
        const map = new Map();
        (timePeriods || []).forEach((period) => {
            if (period?.id != null) {
                map.set(String(period.id), period);
            }
        });
        return map;
    }, [timePeriods]);

    const periodOptions = useMemo(() => {
        const options = [{ id: 'all', label: 'All periods' }];
        (timePeriods || []).forEach((period) => {
            options.push({
                id: String(period.id),
                label: formatPeriodLabel(period)
            });
        });
        if (drafts.some((draft) => getDraftPeriodId(draft) === null)) {
            options.push({ id: '__unassigned__', label: 'Unscoped drafts' });
        }
        return options;
    }, [timePeriods, drafts]);

    const filteredDrafts = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        return (drafts || [])
            .filter((draft) => {
                if (filterPeriodId === 'all') return true;
                if (filterPeriodId === '__unassigned__') {
                    return getDraftPeriodId(draft) === null;
                }
                return String(getDraftPeriodId(draft)) === String(filterPeriodId);
            })
            .filter((draft) => {
                if (!query) return true;
                const period = periodById.get(String(getDraftPeriodId(draft)));
                const haystack = [
                    getDraftTitle(draft),
                    draft?.display_name,
                    draft?.draft_name,
                    draft?.year,
                    period?.name,
                    formatPeriodLabel(period)
                ].filter(Boolean).join(' ').toLowerCase();
                return haystack.includes(query);
            })
            .sort((a, b) => {
                const aTime = new Date(a?.last_modified || 0).getTime();
                const bTime = new Date(b?.last_modified || 0).getTime();
                return bTime - aTime;
            });
    }, [drafts, filterPeriodId, searchQuery, periodById]);

    const isDraftLive = useCallback((draft) => {
        const draftId = getDraftId(draft);
        const periodId = getDraftPeriodId(draft);
        if (!draftId || periodId == null) return false;
        const period = periodById.get(String(periodId));
        const activeId = getPeriodActiveDraftId(period);
        return activeId != null && String(activeId) === String(draftId);
    }, [periodById]);

    const startRename = (draft) => {
        setEditingDraftName(draft.draft_name);
        setEditValue(getDraftTitle(draft));
    };

    const cancelRename = () => {
        setEditingDraftName(null);
        setEditValue('');
    };

    const handleRenameDraft = async (draft) => {
        if (!draftManager || !draft?.draft_name) return;

        const nextName = editValue.trim();
        if (!nextName || nextName === getDraftTitle(draft)) {
            cancelRename();
            return;
        }

        setBusyDraftName(draft.draft_name);
        if (setIsLoading) setIsLoading(true);
        try {
            await draftManager.renameDraft(draft.draft_name, nextName);
            if (setSuccess) {
                setSuccess(`Draft renamed to "${nextName}".`);
                setTimeout(() => setSuccess(null), 3000);
            }
            cancelRename();
            await loadDrafts();
        } catch (err) {
            if (setError) setError('Failed to rename draft: ' + err.message);
        } finally {
            setBusyDraftName(null);
            if (setIsLoading) setIsLoading(false);
        }
    };

    const handleDeleteDraft = async (draft) => {
        if (!draftManager || !draft?.draft_name) return;

        const title = getDraftTitle(draft);
        const isLive = isDraftLive(draft);
        const confirmed = window.confirm(
            `Delete draft "${title}"?${isLive ? ' It is currently live for its period.' : ''} This cannot be undone.`
        );
        if (!confirmed) return;

        const periodId = getDraftPeriodId(draft);
        setBusyDraftName(draft.draft_name);
        if (setIsLoading) setIsLoading(true);
        try {
            if (isLive && assetManager && periodId != null) {
                await assetManager.setPeriodActiveScheduleDraft(periodId, null);
                if (refreshTimePeriods) {
                    await refreshTimePeriods();
                }
            }
            await draftManager.deleteDraft(draft.draft_name);
            if (currentDraftName === draft.draft_name && typeof onCurrentDraftDeleted === 'function') {
                await onCurrentDraftDeleted();
            }
            if (setSuccess) {
                setSuccess(`Draft "${getDraftTitle(draft)}" deleted.`);
                setTimeout(() => setSuccess(null), 3000);
            }
            if (editingDraftName === draft.draft_name) {
                cancelRename();
            }
            await loadDrafts();
        } catch (err) {
            if (setError) setError('Failed to delete draft: ' + err.message);
        } finally {
            setBusyDraftName(null);
            if (setIsLoading) setIsLoading(false);
        }
    };

    const handleOpenDraft = async (draft) => {
        if (!draft?.draft_name) return;

        setBusyDraftName(draft.draft_name);
        if (setIsLoading) setIsLoading(true);
        try {
            if (draftManager) {
                draftManager.setDraftMode(draft.draft_name);
            }
            if (typeof onModeChange === 'function') {
                onModeChange(false, draft.draft_name);
            }
            if (typeof onOpenDraft === 'function') {
                onOpenDraft(draft);
            }
            if (setSuccess) {
                setSuccess(`Opened draft "${getDraftTitle(draft)}".`);
                setTimeout(() => setSuccess(null), 3000);
            }
        } catch (err) {
            if (setError) setError('Failed to open draft: ' + err.message);
        } finally {
            setBusyDraftName(null);
            if (setIsLoading) setIsLoading(false);
        }
    };

    return (
        <div className="mx-auto max-w-6xl px-4">
            <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h2 className="text-xl font-semibold text-gray-900">Draft Manager</h2>
                    <p className="mt-1 text-sm text-gray-500">
                        Browse schedule drafts, rename them, or remove ones you no longer need.
                    </p>
                </div>
                <div className="text-sm text-gray-500">
                    {loadingDrafts ? 'Refreshing…' : `${filteredDrafts.length} of ${drafts.length} drafts`}
                </div>
            </div>

            <div className="mb-5 flex flex-col gap-3 sm:flex-row">
                <div className="relative flex-1">
                    <IoSearchOutline className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input
                        type="search"
                        value={searchQuery}
                        onChange={(event) => setSearchQuery(event.target.value)}
                        placeholder="Search by name or period…"
                        className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </div>
                <select
                    value={filterPeriodId}
                    onChange={(event) => setFilterPeriodId(event.target.value)}
                    className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 sm:min-w-56"
                >
                    {periodOptions.map((option) => (
                        <option key={option.id} value={option.id}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </div>

            {loadingDrafts && drafts.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-300 bg-white px-4 py-16 text-center text-sm text-gray-500">
                    Loading drafts…
                </div>
            ) : filteredDrafts.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-300 bg-white px-4 py-16 text-center text-sm text-gray-500">
                    No drafts match this filter.
                </div>
            ) : (
                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                    {filteredDrafts.map((draft) => {
                        const period = periodById.get(String(getDraftPeriodId(draft)));
                        const isCurrent = draft.draft_name === currentDraftName;
                        const isLive = isDraftLive(draft);
                        const isEditing = editingDraftName === draft.draft_name;
                        const isBusy = busyDraftName === draft.draft_name;
                        const entryCount = Number(draft.entries_count) || 0;

                        return (
                            <article
                                key={draft.id || draft.draft_name}
                                className={`flex flex-col rounded-xl border bg-white p-4 shadow-sm transition-colors ${
                                    isLive
                                        ? 'border-green-300'
                                        : isCurrent
                                            ? 'border-blue-300'
                                            : 'border-gray-200'
                                }`}
                            >
                                <div className="mb-3 flex items-start justify-between gap-2">
                                    <div className="min-w-0 flex-1">
                                        {isEditing ? (
                                            <div className="flex items-center gap-1">
                                                <input
                                                    autoFocus
                                                    type="text"
                                                    value={editValue}
                                                    onChange={(event) => setEditValue(event.target.value)}
                                                    onKeyDown={(event) => {
                                                        if (event.key === 'Enter') {
                                                            event.preventDefault();
                                                            handleRenameDraft(draft);
                                                        }
                                                        if (event.key === 'Escape') {
                                                            event.preventDefault();
                                                            cancelRename();
                                                        }
                                                    }}
                                                    disabled={isBusy}
                                                    className="w-full rounded-md border border-blue-300 px-2 py-1 text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => handleRenameDraft(draft)}
                                                    disabled={isBusy}
                                                    className="rounded-md p-1.5 text-green-700 hover:bg-green-50 disabled:opacity-50"
                                                    title="Save name"
                                                    aria-label="Save name"
                                                >
                                                    <IoCheckmarkOutline className="h-4 w-4" />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={cancelRename}
                                                    disabled={isBusy}
                                                    className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 disabled:opacity-50"
                                                    title="Cancel"
                                                    aria-label="Cancel rename"
                                                >
                                                    <IoCloseOutline className="h-4 w-4" />
                                                </button>
                                            </div>
                                        ) : (
                                            <h3 className="truncate text-base font-semibold text-gray-900" title={formatDraftOptionLabel(draft)}>
                                                {formatDraftOptionLabel(draft)}
                                            </h3>
                                        )}
                                        <p className="mt-1 truncate text-xs text-gray-500">
                                            {formatPeriodLabel(period)}
                                        </p>
                                    </div>
                                    <div className="flex flex-none flex-col items-end gap-1">
                                        {isLive && (
                                            <span className="rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-semibold text-green-700">
                                                Live
                                            </span>
                                        )}
                                        {isCurrent && (
                                            <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-semibold text-blue-700">
                                                Open
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <dl className="mb-4 grid grid-cols-2 gap-3 text-sm">
                                    <div>
                                        <dt className="text-xs uppercase tracking-wide text-gray-400">Entries</dt>
                                        <dd className="mt-0.5 font-medium text-gray-800">{entryCountLabel(entryCount)}</dd>
                                    </div>
                                    <div>
                                        <dt className="text-xs uppercase tracking-wide text-gray-400">Year</dt>
                                        <dd className="mt-0.5 font-medium text-gray-800">{draft.year ?? '—'}</dd>
                                    </div>
                                    <div className="col-span-2">
                                        <dt className="text-xs uppercase tracking-wide text-gray-400">Last modified</dt>
                                        <dd className="mt-0.5 font-medium text-gray-800" title={formatAbsoluteDate(draft.last_modified)}>
                                            {formatRelativeDate(draft.last_modified)}
                                            <span className="ml-2 text-xs font-normal text-gray-400">
                                                {formatAbsoluteDate(draft.last_modified)}
                                            </span>
                                        </dd>
                                    </div>
                                </dl>

                                <div className="mt-auto flex items-center gap-2 border-t border-gray-100 pt-3">
                                    <button
                                        type="button"
                                        onClick={() => handleOpenDraft(draft)}
                                        disabled={isBusy || isEditing}
                                        className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        <IoOpenOutline className="h-4 w-4" />
                                        Open
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => startRename(draft)}
                                        disabled={isBusy || isEditing}
                                        className="inline-flex items-center justify-center rounded-lg border border-gray-300 px-2.5 py-2 text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                                        title="Rename draft"
                                        aria-label={`Rename ${getDraftTitle(draft)}`}
                                    >
                                        <IoCreateOutline className="h-4 w-4" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleDeleteDraft(draft)}
                                        disabled={isBusy || isEditing}
                                        className="inline-flex items-center justify-center rounded-lg border border-red-200 px-2.5 py-2 text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                                        title="Delete draft"
                                        aria-label={`Delete ${getDraftTitle(draft)}`}
                                    >
                                        <IoTrashOutline className="h-4 w-4" />
                                    </button>
                                </div>
                            </article>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default DraftManagerView;
